<template>
  <div>
    <svg
      version="1.1"
      :width="width"
      :height="height"
      :viewbox="viewbox"
    >
      <MultilineText
        :lines="wrappedChartTitle.lines"
        :x="width/2"
        :y="0"
        x-align="center"
        :font="chartTitleFont"
      />
      <MultilineText
        :lines="wrappedChartSubtitle.lines"
        :x="width/2"
        :y="wrappedChartTitle.height + space.belowTitle"
        x-align="center"
        :font="chartSubtitleFont"
      />
      <g :transform="`translate(${bufferLeft}, ${bufferTop})`">
        <line
          v-for="tick in xTicks"
          :key="`grid-${tick}`"
          :x1="xScale(tick)"
          :x2="xScale(tick)"
          :y1="0"
          :y2="plotHeight"
          stroke-width="1"
          stroke="#e6e6e6"
        />
        <text
          v-for="tick in xTicks"
          :key="`top-tick-label-${tick}`"
          :x="xScale(tick)"
          y="-0.5em"
          :font-size="xAxisTickLabelFont.size"
          :font-family="xAxisTickLabelFont.family"
          :fill="xAxisTickLabelFont.color"
          text-anchor="middle"
        >
          {{ xTickFormatter(tick) }}
        </text>
        <text
          :x="plotWidth / 2"
          y="-2em"
          :font-size="xAxisTitleFont.size"
          :font-family="xAxisTitleFont.family"
          :fill="xAxisTitleFont.color"
          text-anchor="middle"
        >
          {{ xAxisTitle }}
        </text>
        <text
          v-for="tick in xTicks"
          :key="`bottom-tick-label-${tick}`"
          :x="xScale(tick)"
          :y="plotHeight"
          dy="0.5em"
          :font-size="xAxisTickLabelFont.size"
          :font-family="xAxisTickLabelFont.family"
          :fill="xAxisTickLabelFont.color"
          dominant-baseline="hanging"
          text-anchor="middle"
        >
          {{ xTickFormatter(tick) }}
        </text>
        <text
          :x="plotWidth / 2"
          :y="plotHeight"
          dy="2em"
          :font-size="xAxisTitleFont.size"
          :font-family="xAxisTitleFont.family"
          :fill="xAxisTitleFont.color"
          dominant-baseline="hanging"
          text-anchor="middle"
        >
          {{ xAxisTitle }}
        </text>
        <text
          :x="majorLabelX"
          y="-2em"
          :font-size="yAxisHeaderFont.size"
          :font-family="yAxisHeaderFont.family"
          :fill="yAxisHeaderFont.color"
          text-anchor="end"
        >
          [{{ majorQuestionId }}]
        </text>
        <MultilineText
          v-for="(category, i) in wrappedCategories"
          :key="`category-axis-label-${i}`"
          :lines="category.lines"
          :x="majorLabelX"
          :y="yScaleMajor(i) - (category.height/2)"
          x-align="right"
          :font="categoryFont"
        />
        <template v-if="hasMinorLabels">
          <text
            :x="minorLabelX"
            y="-2em"
            :font-size="yAxisHeaderFont.size"
            :font-family="yAxisHeaderFont.family"
            :fill="yAxisHeaderFont.color"
            text-anchor="end"
          >
            [{{ minorQuestionId }}]
          </text>
          <line
            v-for="(category, i) in chartData.categories.slice(0, -1)"
            :key="`major-separator-${i}`"
            :x1="-bufferLeft"
            :y1="yScaleMajor(i) + majorGroupHeight/2 + space.betweenMajorGroups/2"
            :x2="plotWidth + bufferRight"
            :y2="yScaleMajor(i) + majorGroupHeight/2 + space.betweenMajorGroups/2"
            stroke-width="1"
            stroke="#e6e6e6"
          />
          <g
            v-for="(category, i) in chartData.categories"
            :key="`major-bar-group-${i}`"
            :transform="`translate(0, ${yScaleMajor(i) - majorGroupHeight/2})`"
          >
            <MultilineText
              v-for="(subCategory, j) in wrappedSubCategories"
              :key="`subCategory-axis-label-${i}-${j}`"
              :lines="subCategory.lines"
              :x="minorLabelX"
              :y="yScaleMinor(j) - subCategory.height/2"
              x-align="right"
              :font="subCategoryFont"
            />
            <GraphChartBar
              v-for="(d, j) in chartData.data[i]"
              :key="`bar-${i}-${j}`"
              :cell="d"
              :x-scale="xScale"
              :y="yScaleMinor(j)"
              :height="space.barHeight"
              :color="colorScale(chartData.subCategories[j])"
              :show-confidence-interval="showConfidenceInterval"
              :show-percentages="showPercentages"
              :suppression-symbol="suppressionSymbol"
              :suppression-symbol-font="suppressionSymbolFont"
              :benchmark-symbol="benchmarkSymbol"
              :benchmark-symbol-font="benchmarkSymbolFont"
              @onMouseEnter="onMouseEnterBar($event.coords, i, j)"
              @onMouseLeave="onMouseLeaveBar()"
            />
          </g>
        </template>
        <template v-else>
          <GraphChartBar
            v-for="(d, i) in chartData.data"
            :key="`bar-${i}`"
            :cell="d[0]"
            :x-scale="xScale"
            :y="yScaleMajor(i)"
            :height="space.barHeight"
            :color="colorScale(chartData.categories[i])"
            :show-confidence-interval="showConfidenceInterval"
            :show-percentages="showPercentages"
            :suppression-symbol="suppressionSymbol"
            :suppression-symbol-font="suppressionSymbolFont"
            :benchmark-symbol="benchmarkSymbol"
            :benchmark-symbol-font="benchmarkSymbolFont"
            @onMouseEnter="onMouseEnterBar($event.coords, i, 0)"
            @onMouseLeave="onMouseLeaveBar()"
          />
        </template>
      </g>
    </svg>
    <div
      id="dashboard-chart-tooltip"
      class="dashboard-chart-tooltip"
      v-if="showTooltip"
      :style="tooltipStyle"
    >
      <p><b>{{ tooltipHeader }}</b></p>
      <template v-if="tooltipCell.suppressed">
        <p>Results have been suppressed.</p>
      </template>
      <template v-else>
        <dl>
          <div>
            <dt>{{ tooltipPercentageLabel }}:</dt>
            <dd>{{ prettyPrintProportion(tooltipCell.proportion) }}</dd>
          </div>
          <div>
            <dt>95% CI for percentage:</dt>
            <dd>{{ prettyPrintProportionCI(tooltipCell.ciProportion) }}</dd>
          </div>
          <div v-if="tooltipCell.benchmark">
            <dt>CG-CAHPS benchmark for percentage:</dt>
            <dd>{{ tooltipCell.benchmark }}</dd>
          </div>
          <div>
            <dt>Sample size:</dt>
            <dd>{{ prettyPrintSampleSize(tooltipCell.sampleSize) }}</dd>
          </div>
          <div>
            <dt>Population size:</dt>
            <dd>{{ prettyPrintPopulationSize(tooltipCell.numerator) }}</dd>
          </div>
          <div>
            <dt>95% CI for population size:</dt>
            <dd>{{ prettyPrintPopulationSizeCI(tooltipCell.ciNumerator) }}</dd>
          </div>
        </dl>
      </template>
    </div>
  </div>
</template>

<script>
import { scaleLinear, scaleOrdinal } from 'd3-scale'
import { debounce } from 'debounce'
import GraphChartBar from './GraphChartBar.vue'
import MultilineText from './MultilineText.vue'
import {
  prettyPrintProportion,
  prettyPrintProportionCI,
  prettyPrintSampleSize,
  prettyPrintPopulationSize,
  prettyPrintPopulationSizeCI
} from '../utils.js'

export default {
  name: 'GraphChart',
  components: { GraphChartBar, MultilineText },
  props: {
    chart: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      width: 0,
      showTooltip: false,
      tooltipCell: null,
      tooltipCellCategory: null,
      tooltipCellSubCategory: null,
      tooltipTargetCoords: null,
      xAxisTitleFont: { size: '12px', family: 'Source Sans Pro, sans-serif', lineHeight: '1.2em', color: '#666666' },
      xAxisTickLabelFont: { size: '11px', family: 'Source Sans Pro, sans-serif', lineHeight: '1.2em', color: '#666666' },
      yAxisHeaderFont: { size: '12px', family: 'Source Sans Pro, sans-serif', lineHeight: '1.2em', color: '#666666' },
      chartTitleFont: { size: '18px', family: 'Merriweather, serif', lineHeight: '1.2em', color: '#112e51' },
      chartSubtitleFont: { size: '12px', family: 'Merriweather, serif', lineHeight: '1.2em', color: '#112e51' },
      categoryFont: { size: '14px', family: 'Source Sans Pro, sans-serif', lineHeight: '1.1em', color: '#272727' },
      subCategoryFont: { size: '14px', family: 'Source Sans Pro, sans-serif', lineHeight: '1.1em', color: '#272727' },
      suppressionSymbolFont: { size: '16px', family: 'Merriweather, serif', lineHeight: '1.1em', color: '#323a45' },
      benchmarkSymbolFont: { size: '20px', family: 'Roboto Mono Dagger, sans-serif', lineHeight: '1.1em', color: '#636363' },
      space: {
        belowTitle: 16,
        belowSubtitle: 24,
        aboveFootnotes: 12,
        maxMajorLabelWidth: 125,
        maxMinorLabelWidth: 125,
        maxMajorOnlyLabelWidth: 175,
        betweenYAxisAndLabel: 16,
        betweenMinorAndMajorLabel: 16,
        betweenXAxisAndMajorOnlyRow: 8,
        betweenXAxisAndMajorGroup: 20,
        betweenMinorRows: 6,
        betweenMajorGroups: 40,
        betweenMajorOnlyRows: 8,
        barHeight: 20,
        rightOfPlot: 10
      }
    }
  },
  computed: {
    height () {
      return this.plotHeight + this.bufferTop + this.bufferBottom
    },
    viewbox () {
      return `0 0 ${this.width} ${this.height}`
    },
    hasMinorLabels () {
      return this.chartData.subCategories
    },
    majorLabelWidth () {
      const labelWidths = this.chartData.categories.map(c => calculateTextWidth(c, this.categoryFont))
      const maxLabelWidth = Math.max(...labelWidths)
      const headerWidth = calculateTextWidth(`[${this.majorQuestionId}]`, this.yAxisHeaderFont)
      const maxAllowedWidth = this.hasMinorLabels ? this.space.maxMajorLabelWidth : this.space.maxMajorOnlyLabelWidth
      return Math.max(headerWidth, Math.min(maxLabelWidth, maxAllowedWidth))
    },
    minorLabelWidth () {
      if (this.hasMinorLabels) {
        const labelWidths = this.chartData.subCategories.map(c => calculateTextWidth(c, this.subCategoryFont))
        const maxLabelWidth = Math.max(...labelWidths)
        const headerWidth = calculateTextWidth(`[${this.minorQuestionId}]`, this.yAxisHeaderFont)
        const maxAllowedWidth = this.space.maxMinorLabelWidth
        return Math.max(headerWidth, Math.min(maxLabelWidth, maxAllowedWidth))
      } else {
        return 0
      }
    },
    maxMajorLabelHeight () {
      return Math.max(...this.wrappedCategories.map(c => c.height), this.space.barHeight)
    },
    majorLabelHeights () {
      return this.wrappedCategories.map(c => this.maxMajorLabelHeight)
    },
    cumulativeMajorLabelHeights () {
      return this.majorLabelHeights.reduce((arr, height) => {
        arr.push(arr[arr.length - 1] + height)
        return arr
      }, [0])
    },
    sumMajorLabelHeights () {
      return this.cumulativeMajorLabelHeights[this.cumulativeMajorLabelHeights.length - 1]
    },
    minorLabelHeights () {
      if (this.chartData.subCategories) {
        return this.wrappedSubCategories.map(c => Math.max(this.space.barHeight, c.height))
      }
      return []
    },
    cumulativeMinorLabelHeights () {
      if (this.chartData.subCategories) {
        return this.minorLabelHeights.reduce((arr, height) => {
          arr.push(arr[arr.length - 1] + height)
          return arr
        }, [0])
      }
      return [0]
    },
    sumMinorLabelHeights () {
      return this.cumulativeMinorLabelHeights[this.cumulativeMinorLabelHeights.length - 1]
    },
    minorGroupHeight () {
      if (this.hasMinorLabels) {
        const minorGaps = this.chartData.data[0].length - 1
        return (
          this.sumMinorLabelHeights +
          (minorGaps * this.space.betweenMinorRows)
        )
      } else {
        return 0
      }
    },
    majorGroupHeight () {
      if (this.hasMinorLabels) {
        return Math.max(this.minorGroupHeight, this.maxMajorLabelHeight)
      } else {
        return 0
      }
    },
    majorLabelX () {
      if (this.hasMinorLabels) {
        return -(
          this.space.betweenMinorAndMajorLabel +
          this.minorLabelWidth +
          this.space.betweenYAxisAndLabel
        )
      } else {
        return -this.space.betweenYAxisAndLabel
      }
    },
    minorLabelX () {
      return -this.space.betweenYAxisAndLabel
    },
    bufferTop () {
      return (
        this.wrappedChartTitle.height +
        this.space.belowTitle +
        this.wrappedChartSubtitle.height +
        this.space.belowSubtitle +
        this.xAxisTickAndTitleHeight
      )
    },
    bufferBottom () {
      return this.xAxisTickAndTitleHeight + this.space.aboveFootnotes
    },
    bufferLeft () {
      if (this.hasMinorLabels) {
        return (
          this.majorLabelWidth +
          this.space.betweenMinorAndMajorLabel +
          this.minorLabelWidth +
          this.space.betweenYAxisAndLabel
        )
      } else {
        return this.majorLabelWidth + this.space.betweenYAxisAndLabel
      }
    },
    bufferRight () {
      return this.space.rightOfPlot
    },
    plotWidth () {
      return this.width - this.bufferLeft - this.bufferRight
    },
    plotHeight () {
      // Insert a major gap between each major label/group.
      const majorGaps = this.chartData.data.length - 1

      if (this.hasMinorLabels) {
        return (2 * this.space.betweenXAxisAndMajorGroup) +
          (this.chartData.data.length * this.majorGroupHeight) +
          (majorGaps * this.space.betweenMajorGroups)
      } else {
        return (2 * this.space.betweenXAxisAndMajorOnlyRow) +
          this.sumMajorLabelHeights +
          (majorGaps * this.space.betweenMajorOnlyRows)
      }
    },
    xScale () {
      let domain
      if (this.showPercentages) {
        domain = [0, 100]
      } else {
        let maxX = 0
        this.chartData.data.forEach(byGroup => {
          byGroup.forEach(cell => {
            if (cell.isSuppressed) {
              return
            }
            if (!isNaN(cell.ciNumerator.upper)) {
              maxX = Math.max(maxX, cell.ciNumerator.upper)
            } else {
              maxX = Math.max(maxX, cell.numerator)
            }
          })
        })
        domain = [0, maxX]
      }
      return scaleLinear().domain(domain).range([0, this.plotWidth]).nice()
    },
    xTickCount () {
      return this.width > 500 ? 11 : 6
    },
    xTicks () {
      return this.xScale.ticks(this.xTickCount)
    },
    xTickFormatter () {
      return this.xScale.tickFormat(this.xTickCount, 's')
    },
    xAxisTickAndTitleHeight () {
      return 40
    },
    yScaleMajor () {
      if (this.hasMinorLabels) {
        return i => {
          return (
            this.space.betweenXAxisAndMajorGroup +
            i * this.space.betweenMajorGroups +
            i * this.majorGroupHeight +

            // Move to the center of this label.
            this.majorGroupHeight / 2
          )
        }
      } else {
        return i => {
          return (
            this.space.betweenXAxisAndMajorOnlyRow +
            i * this.space.betweenMajorOnlyRows +

            // Element i of this array is the cumulative height of all labels before
            // label i.
            this.cumulativeMajorLabelHeights[i] +

            // Move to the center of this label.
            this.majorLabelHeights[i] / 2
          )
        }
      }
    },
    yScaleMinor () {
      return i => {
        return (
          // Account for the rare case where the major label is so long that it
          // wraps to a height taller than the combined minor labels. In that
          // case, we want to center the collection of minor labels within the
          // space afforded to the major label, so we need to shift the minor
          // collection down.
          (this.majorGroupHeight - this.minorGroupHeight) / 2 +

          // Use i instead of i+1 because we don't want to add space above the
          // first minor row. The space between major groups already gives us that.
          i * this.space.betweenMinorRows +

          // Element i of this array is the cumulative height of all labels before
          // label i.
          this.cumulativeMinorLabelHeights[i] +

          // Move to the center of this label.
          this.minorLabelHeights[i] / 2
        )
      }
    },
    colorScale () {
      const colors = ['#204593', '#2e8540', '#fdb81e', '#b0d324', '#fb6a4a', '#4c2c92', '#981b1e', '#267cb8', '#5dc01d', '#9976ec', '#d62a23']
      if (this.hasMinorLabels) {
        return scaleOrdinal().domain(this.chartData.subCategories).range(colors)
      }
      return scaleOrdinal().domain(this.chartData.categories).range([colors[0]])
    },
    xAxisTitle () {
      return this.showPercentages
        ? (this.hasMinorLabels ? `Percentage within [${this.majorQuestionId}] category` : 'Percentage')
        : 'Population Size'
    },
    showPercentages () {
      return this.getFilter('showPercentage')
    },
    showConfidenceInterval () {
      return this.getFilter('confidenceInterval')
    },
    chartData () {
      return this.getChartData(this.chart.key)
    },
    majorQuestionId () {
      return this.chartData.categoryId
    },
    minorQuestionId () {
      return this.chartData.subCategoryId
    },
    wrappedChartTitle () {
      return wrapText(this.chartData.title, this.width, this.chartTitleFont)
    },
    wrappedChartSubtitle () {
      return wrapText(this.chartData.subtitle, this.width, this.chartSubtitleFont)
    },
    wrappedCategories () {
      return this.chartData.categories.map(category => {
        return wrapText(category, this.majorLabelWidth, this.categoryFont)
      })
    },
    wrappedSubCategories () {
      if (this.hasMinorLabels) {
        return this.chartData.subCategories.map(c => {
          return wrapText(c, this.minorLabelWidth, this.subCategoryFont)
        })
      } else {
        return null
      }
    },
    tooltipStyle () {
      const vw = window.visualViewport.width
      const x = this.tooltipTargetCoords.x
      const left = Math.min(vw - 400, x)

      return {
        left: `${left}px`,
        top: `${this.tooltipTargetCoords.bbox.top}px`,
        transform: 'translate(0, -100%)'
      }
    },
    tooltipHeader () {
      const majorCat = this.tooltipCellCategory
      const minorCat = this.tooltipCellSubCategory
      if (this.minorQuestionId) {
        return `[${this.majorQuestionId}] ${majorCat}, [${this.minorQuestionId}] ${minorCat}`
      } else {
        return `[${this.majorQuestionId}] ${majorCat}`
      }
    },
    tooltipPercentageLabel () {
      const majorCat = this.tooltipCellCategory
      if (this.minorQuestionId) {
        return `Percentage within [${this.majorQuestionId}] ${majorCat}`
      } else {
        return 'Percentage'
      }
    },
    suppressionSymbol () {
      return this.chartData.footnotes.suppressed ? this.chartData.footnotes.suppressed.symbol : ''
    },
    benchmarkSymbol () {
      return this.chartData.footnotes.benchmark.symbol
    }
  },
  methods: {
    onMouseEnterBar (coords, i, j) {
      this.showTooltip = true
      this.tooltipCell = this.chartData.data[i][j]
      this.tooltipCellCategory = this.chartData.categories[i]
      if (this.hasMinorLabels) {
        this.tooltipCellSubCategory = this.chartData.subCategories[j]
      }
      this.tooltipTargetCoords = coords
    },
    onMouseLeaveBar () {
      this.showTooltip = false
      this.tooltipCell = null
      this.tooltipCellCategory = null
      this.tooltipCellSubCategory = null
      this.tooltipTargetCoords = null
    },
    setWidth () {
      const el = document.getElementById('dashboard-chart-container')
      const rect = el.getBoundingClientRect()
      this.width = rect.width
      this.resizeCounter++
    },
    prettyPrintProportion,
    prettyPrintProportionCI,
    prettyPrintSampleSize,
    prettyPrintPopulationSize,
    prettyPrintPopulationSizeCI
  },
  created () {
    this.setWidth()
    this.debounceSetWidth = debounce(this.setWidth, 100)
    window.addEventListener('resize', this.debounceSetWidth)
  },
  destroyed () {
    window.removeEventListener('resize', this.debounceSetWidth)
  }
}

function calculateTextWidth (text, font) {
  const ns = 'http://www.w3.org/2000/svg'
  const svgElement = document.createElementNS(ns, 'svg')
  const textElement = document.createElementNS(ns, 'text')

  svgElement.setAttribute('width', 0)
  svgElement.setAttribute('height', 0)
  svgElement.setAttribute('aria-hidden', true)
  textElement.setAttribute('font-family', font.family)
  textElement.setAttribute('font-size', font.size)

  document.getElementById('dashboard-chart-container').append(svgElement)
  svgElement.append(textElement)
  textElement.append(text)

  const { width } = textElement.getBBox()

  svgElement.remove()

  return width
}

function wrapText (text, maxWidth, font) {
  const ns = 'http://www.w3.org/2000/svg'
  const svgElement = document.createElementNS(ns, 'svg')
  const textElement = document.createElementNS(ns, 'text')
  const tspanElement = document.createElementNS(ns, 'tspan')
  const textNode = document.createTextNode('')

  svgElement.setAttribute('width', 0)
  svgElement.setAttribute('height', 0)
  svgElement.setAttribute('aria-hidden', true)
  textElement.setAttribute('font-family', font.family)
  textElement.setAttribute('font-size', font.size)

  document.getElementById('dashboard-chart-container').append(svgElement)
  svgElement.append(textElement)
  textElement.append(tspanElement)
  tspanElement.append(textNode)

  // This algorithm's approach is to split the text into words, then try adding
  // one word at a time to the current line. If the measured length of the line
  // exceeds the max width, then remove the last word from the current line and
  // start a new line with that word.
  //
  // The naive way to split the text into words is to split on spaces. However,
  // our text has several instances of slash-separated words like
  // "hassle/paperwork/benefit", which won't fit on a single line without
  // additional splitting. Therefore, split on spaces, slashes, and hyphens.
  // The regex is written so that the delimiters are returned as separate
  // tokens. A positive lookbehind regex (/(?<=[ /-])/g) would have worked as
  // well, but those aren't supported by Safari at the time of writing.
  //
  // Even after splitting on spaces, slashes, and hyphens, if a word is so long
  // that it won't fit on a line, this algorithm will as a last resort break the
  // word somewhere in the middle. This part of the algorithm won't handle a
  // super-long word that must wrap to a third line.

  const words = text.split(/([\s/-]+)/).filter(w => w !== '')
  let currentLineWords = []
  const lines = []

  for (let i = 0; i < words.length; i++) {
    currentLineWords.push(words[i])
    textNode.data = currentLineWords.join('').replace(/\s+$/, '')
    if (textElement.getBBox().width > maxWidth) {
      currentLineWords.pop()
      if (currentLineWords.length > 0) {
        lines.push(currentLineWords.join('').replace(/\s+$/, ''))
        currentLineWords = []
        i--
      } else {
        for (let j = 0; j < words[i].length; j++) {
          currentLineWords.push(words[i][j])
          textNode.data = currentLineWords.join('').replace(/\s+$/, '')
          if (textElement.getBBox().width > maxWidth) {
            currentLineWords.pop()
            lines.push(currentLineWords.join('').replace(/\s+$/, ''))
            currentLineWords = []
            j--
          }
        }
      }
    }
  }
  lines.push(currentLineWords.join('').replace(/\s+$/, ''))

  tspanElement.remove()
  lines.forEach((line, i) => {
    const lineTspanElement = document.createElementNS(ns, 'tspan')
    lineTspanElement.setAttribute('x', 0)
    lineTspanElement.setAttribute('dy', i === 0 ? 0 : font.lineHeight)
    lineTspanElement.append(line)
    textElement.append(lineTspanElement)
  })
  const { width, height } = textElement.getBBox()

  svgElement.remove()

  return { text, lines, width, height }
}
</script>

<style lang="scss" scoped>
.dashboard-chart-tooltip {
  position: fixed;
  max-width: 30em;
  padding: 6px 10px;
  background-color: #fff;
  border: solid 1px #b9b9b9;
  border-radius: 4px;
  box-shadow: 2px 2px 6px #cccccc;

  p {
    margin-bottom: 0;
  }

  dl {
    margin-bottom: 0;
  }

  dt {
    font-weight: normal;
    display: inline-block;
  }

  dd {
    margin: 0 0 0 0.5em;
    display: inline-block;
  }
}
</style>
